import React, { ComponentProps, ReactNode, Suspense, lazy, useCallback } from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';

import App from '@/app/App';
import EmbeddedApp from '@/app/bootstrap/embed-session/EmbeddedApp';
import { getEmbedToken } from '@/app/bootstrap/embed-session/embed-session';
import RequireAuth from '@/app/routing/guards/require-auth/RequireAuth';
import NonExistent from '@/app/routing/not-found/NotFound';
import AccountPage from '@/features/account/components/account-page/AccountPage';
import CliLogin from '@/features/auth/components/cli-login/CliLogin';
import GithubCallback from '@/features/auth/components/github-callback/GithubCallback';
import Login from '@/features/auth/components/login/Login';
import Register from '@/features/auth/components/register/Register';
import AppsList from '@/features/dashboard/components/apps-list/AppsList';
import GroupsPage from '@/features/org/components/groups/groups-page/GroupsPage';
import MembersPage from '@/features/org/components/members/members-page/MembersPage';
import RedeemInvitePage from '@/features/org/components/members/redeem-invite-page/RedeemInvitePage';
import PermissionsPage from '@/features/org/components/permissions/permissions-page/PermissionsPage';
import OrgSettingsPage from '@/features/org/components/settings/org-settings-page/OrgSettingsPage';
import AuthzForbiddenBanner from '@/shared/components/banners/authz-forbidden-banner/AuthzForbiddenBanner';
import EmbedSessionBanner from '@/shared/components/banners/embed-session-banner/EmbedSessionBanner';
import type { RouterData, QueryParams } from '@/shared/types/tile-packet.types';

/**
 * Lazy-load DesignGallery so its 130KB tile-ui.css ends up in a route-
 * specific chunk. mini-css-extract-plugin's runtime then auto-injects a
 * `<link>` for that chunk's CSS only when /design is visited — no manual
 * `<link>` in index.html, no CSS shipped to users who never open /design.
 */
const DesignGallery = lazy(
  () =>
    import(
      /* webpackChunkName: "DesignGallery" */ '@/shared/components/design-gallery/DesignGallery'
    ),
);

function LazyDesignGallery() {
  return (
    <Suspense fallback={null}>
      <DesignGallery />
    </Suspense>
  );
}

/**
 * OTA bundles console. Lazy-loaded so its CSS + tile-push client ship only
 * on the /apps/:appId/ota route. Talks directly to tile-push — see
 * docs/OTA_CONSOLE.md.
 */
const OTAPage = lazy(
  () => import(/* webpackChunkName: "OTAPage" */ '@/features/ota/components/ota-page/OtaPage'),
);

function LazyOTAPage() {
  return (
    <Suspense fallback={null}>
      <OTAPage />
    </Suspense>
  );
}

type RouterProps = {
  data: RouterData;
  queryParams: QueryParams;
  userAgent: string;
};

export default function Router(props: RouterProps) {
  // SPA-only now (Koa SSR retired): 404s just render <NonExistent />. The old
  // server-context branch that set the HTTP status/cache headers is gone.
  const render404 = useCallback(() => <NonExistent />, []);

  /**
   * Editor routes (/, /:id, /@user/proj+) require an `appId` query param.
   * Without it, send the user to the apps dashboard so they can pick one.
   * The `app` route is meaningless without an app context — there's no
   * default project to load anymore.
   */
  function renderEditor(routeProps: any) {
    const { data } = props;
    const isEmbedded = routeProps.location.pathname.split('/')[1] === 'embedded';

    // Editor (non-embedded) needs an appId — bounce to /apps if missing.
    if (!isEmbedded && !props.queryParams?.appId) {
      return <Redirect to="/apps" />;
    }

    if (!data || data.type !== 'success') {
      return render404();
    }

    const appProps: ComponentProps<typeof App | typeof EmbeddedApp> = {
      ...routeProps,
      userAgent: props.userAgent,
      query: props.queryParams,
      tilePacket: data.tilePacket,
      defaults: data.defaults,
    };

    return isEmbedded ? <EmbeddedApp {...appProps} /> : <App {...appProps} />;
  }

  /**
   * Wrap any auth-required render function so we don't have to nest
   * `<RequireAuth>` JSX inside every <Route render={…}> below.
   */
  const guarded = (renderFn: (routeProps: any) => ReactNode) =>
    function GuardedRender(routeProps: any) {
      return <RequireAuth>{renderFn(routeProps)}</RequireAuth>;
    };

  /**
   * /embed/app/:id — the editor inside a host product's iframe.
   *
   * Deliberately NOT wrapped in <RequireAuth>: there is no cookie session
   * in a cross-site iframe, and RequireAuth would bounce to /login. Auth
   * is the embed token captured by initEmbedSession() (client/index.tsx)
   * and sent as x-user-token on every same-origin request — the backend
   * enforces it per-call, scoped to exactly this app. Without a token the
   * shell still renders but every API call 401s, so fail fast with a
   * plain message instead.
   */
  function renderEmbedEditor(routeProps: any) {
    const { data } = props;
    if (!getEmbedToken()) {
      return (
        <div style={{ padding: 32, fontFamily: 'sans-serif', color: '#444' }}>
          <h3 style={{ margin: '0 0 8px' }}>Embed session missing its token</h3>
          <p style={{ margin: 0 }}>
            This page must be opened by a host application that provides a session token in the URL
            fragment.
          </p>
        </div>
      );
    }
    if (!props.queryParams?.appId || !data || data.type !== 'success') {
      return render404();
    }
    const appProps: ComponentProps<typeof App> = {
      ...routeProps,
      userAgent: props.userAgent,
      query: props.queryParams,
      tilePacket: data.tilePacket,
      defaults: data.defaults,
    };
    return <App {...appProps} />;
  }

  return (
    <>
      {/* Global 403 toast — mounted at the Router root so it catches
          authz denials from ANY page (dashboard, editor, settings) via
          both apiFetch and the raw-fetch interceptor. */}
      <AuthzForbiddenBanner />
      {/* Embed session status — silent unless the host's token lapses. */}
      <EmbedSessionBanner />
      <Switch>
        {/* ────────────────── Public auth pages (no guard) ────────────────── */}
        <Route exact path="/login" component={Login} />
        <Route exact path="/register" component={Register} />
        {/* Hands the current session to a locally-running `tile` CLI. Public
          because it bounces to /login itself when there's no session — it
          must survive that round trip with its query params intact. */}
        <Route exact path="/cli-login" component={CliLogin} />

        {/* ────────────────── Authed dashboard ─────────────────────────────── */}
        {/* AppsList already wraps itself in <AuthRoute> for the user object,
          but we add RequireAuth here too for defense in depth — anyone
          dropping a child component in dashboardPages won't accidentally
          ship it unguarded. */}
        {/* Account settings — top level, not under /o/:slug, because nothing
          here is org-scoped. AccountPage wraps itself in <AuthRoute>. */}
        <Route exact path="/account" component={AccountPage} />

        <Route exact path="/apps" component={AppsList} />
        <Route exact path="/dashboard" component={AppsList} />

        {/* /apps/:appId/ota — OTA bundles console. Must come before the
          /:id editor catch-all so it wins the match. */}
        <Route
          exact
          path="/apps/:appId/ota"
          render={guarded(() => (
            <LazyOTAPage />
          ))}
        />

        {/* ────────────────── Org-scoped pages ──────────────────────────────── */}
        {/* /o/:slug/dashboard is the org-aware dashboard URL — the switcher
          pushes history here on switch so deep links round-trip. The
          unprefixed /dashboard remains valid and falls back to whatever
          activeOrg slug is in the cookie. */}
        <Route
          exact
          path="/o/:slug/dashboard"
          render={guarded(() => (
            <AppsList />
          ))}
        />
        <Route
          exact
          path="/o/:slug/apps"
          render={guarded(() => (
            <AppsList />
          ))}
        />

        {/* Legacy single-page URLs — keep working but they all render
          inside the new SettingsLayout via the tabbed wrappers below. */}
        <Route
          exact
          path="/o/:slug/members"
          render={guarded(() => (
            <MembersPage />
          ))}
        />
        {/* /o/:slug/settings → /o/:slug/settings/org (default tab). */}
        <Route
          exact
          path="/o/:slug/settings"
          render={(rp) => (
            <Redirect to={`/o/${(rp.match.params as { slug: string }).slug}/settings/org`} />
          )}
        />

        {/* New settings sidebar — Members / Groups / Permissions / Org. */}
        <Route
          exact
          path="/o/:slug/settings/members"
          render={guarded(() => (
            <MembersPage />
          ))}
        />
        <Route
          exact
          path="/o/:slug/settings/groups"
          render={guarded(() => (
            <GroupsPage />
          ))}
        />
        <Route
          exact
          path="/o/:slug/settings/permissions"
          render={guarded(() => (
            <PermissionsPage />
          ))}
        />
        <Route
          exact
          path="/o/:slug/settings/org"
          render={guarded(() => (
            <OrgSettingsPage />
          ))}
        />

        {/* Invite redeem — auth-gated; the page itself bounces to /login
          with ?next= when the user isn't signed in. */}
        <Route
          exact
          path="/invite/:token"
          render={guarded(() => (
            <RedeemInvitePage />
          ))}
        />

        {/* ────────────────── GitHub OAuth callback ────────────────────────── */}
        {/* GitHub's registered redirect URI sends users here with ?code & ?state.
          Auth-gated so we can verify the originating user; the page exchanges
          the code, opens the repo picker, and routes the user to the editor. */}
        <Route
          exact
          path="/github-callback"
          render={guarded(() => (
            <GithubCallback />
          ))}
        />

        {/* /design — visual gallery of every panel + control under
          docs/tile-ui (mounted with mock props for design review). Lazy-
          loaded so its scoped CSS ships only on this route. Auth-gated
          like the rest of the app. */}
        <Route
          exact
          path="/design"
          render={guarded(() => (
            <LazyDesignGallery />
          ))}
        />

        {/* ────────────────── Host-product editor embed ─────────────────────── */}
        {/* Token-authenticated (x-user-token), no cookie session — see
          renderEmbedEditor. Must precede the /:id editor catch-all. */}
        <Route exact path="/embed/app/:id" render={renderEmbedEditor} />

        {/* ────────────────── Embedded preview iframe routes ───────────────── */}
        {/* Embedded routes are loaded inside iframes on 3rd-party docs sites.
          We require auth here too — if you ever need a public embed flow,
          carve out a specific shareable token route instead of opening up
          the iframe surface. */}
        <Route exact path="/embedded/@:username/:projectName+" render={guarded(renderEditor)} />
        <Route exact path="/embedded/:id" render={guarded(renderEditor)} />
        <Route exact path="/embedded" render={guarded(renderEditor)} />

        {/* ────────────────── Editor (auth + appId required) ───────────────── */}
        <Route exact path="/@:username/:projectName+" render={guarded(renderEditor)} />
        <Route exact path="/:id" render={guarded(renderEditor)} />
        <Route exact path="/" render={guarded(renderEditor)} />

        <Route render={render404} />
      </Switch>
    </>
  );
}
